<script>
// Reusable Google Maps heat-glow overlay.
//
// Renders one radial-gradient overlay per { area, lat, lng, spend,
// impressions, clicks } row. The center color interpolates from yellow
// (lowest metric value) to red (highest), and the radius grows with the
// metric, so high-value districts feel both hotter and bigger.
//
// Props
//   data:    Array of points (required).
//   metric:  'spend' | 'impressions' | 'clicks' (default 'spend').
//   height:  CSS height for the map container (default '560px').
//   center:  { lat, lng } map center (default Riyadh).
//   zoom:    Initial zoom level (default 11).
//   markers: Whether to draw a marker dot per point (default true).

export default {
  name: 'AreaHeatmap',
  props: {
    data: { type: Array, required: true },
    metric: { type: String, default: 'spend' },
    height: { type: String, default: '560px' },
    center: {
      type: Object,
      default: () => ({ lat: 24.7136, lng: 46.6753 }),
    },
    zoom: { type: Number, default: 11 },
    markers: { type: Boolean, default: true },
  },
  created() {
    // Hold Google Maps objects on the instance, NOT in data(), so Vue's
    // reactivity proxy does not wrap them and break the Maps API internals.
    this.$mapObject = null
    this.$markers = []
    this.$glowOverlays = []
    this.$GlowOverlay = null
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    this.destroyMap()
  },
  watch: {
    data() {
      this.rerender()
    },
    metric() {
      this.applyGlowLayer()
    },
  },
  methods: {
    initMap() {
      if (!this.$refs.mapEl) return
      this.$gmapApiPromiseLazy().then(() => {
        // eslint-disable-next-line no-undef
        this.$mapObject = new google.maps.Map(this.$refs.mapEl, {
          zoom: this.zoom,
          center: this.center,
          mapTypeId: 'roadmap',
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
        })
        this.defineGlowOverlay()
        this.drawMarkers()
        this.applyGlowLayer()
      })
    },
    defineGlowOverlay() {
      if (this.$GlowOverlay) return
      // eslint-disable-next-line no-undef
      const OverlayView = google.maps.OverlayView
      function GlowOverlay(position, color, radiusMeters) {
        this.position = position
        this.color = color
        this.radiusMeters = radiusMeters
        this.div = null
      }
      GlowOverlay.prototype = new OverlayView()
      GlowOverlay.prototype.onAdd = function () {
        const div = document.createElement('div')
        div.style.position = 'absolute'
        div.style.pointerEvents = 'none'
        div.style.borderRadius = '50%'
        div.style.mixBlendMode = 'multiply'
        div.style.background =
          'radial-gradient(circle, ' +
          this.color + ' 0%, ' +
          this.color.replace(/[\d.]+\)$/, '0.65)') + ' 25%, ' +
          this.color.replace(/[\d.]+\)$/, '0.35)') + ' 55%, ' +
          this.color.replace(/[\d.]+\)$/, '0.0)') + ' 80%)'
        this.div = div
        this.getPanes().overlayLayer.appendChild(div)
      }
      GlowOverlay.prototype.draw = function () {
        if (!this.div) return
        const projection = this.getProjection()
        if (!projection) return
        const pos = projection.fromLatLngToDivPixel(this.position)
        const lat = this.position.lat()
        const zoom = this.getMap().getZoom()
        const metersPerPixel =
          (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)
        const pixelRadius = this.radiusMeters / metersPerPixel
        this.div.style.left = (pos.x - pixelRadius) + 'px'
        this.div.style.top = (pos.y - pixelRadius) + 'px'
        this.div.style.width = (pixelRadius * 2) + 'px'
        this.div.style.height = (pixelRadius * 2) + 'px'
      }
      GlowOverlay.prototype.onRemove = function () {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div)
        }
        this.div = null
      }
      this.$GlowOverlay = GlowOverlay
    },
    drawMarkers() {
      this.clearMarkers()
      if (!this.markers || !this.$mapObject) return
      this.$markers = this.data.map((p) => {
        // eslint-disable-next-line no-undef
        const marker = new google.maps.Marker({
          position: { lat: p.lat, lng: p.lng },
          map: this.$mapObject,
          title: p.area,
          zIndex: 5,
          icon: {
            // eslint-disable-next-line no-undef
            path: google.maps.SymbolPath.CIRCLE,
            scale: 3,
            fillColor: '#1d2939',
            fillOpacity: 0.95,
            strokeColor: '#ffffff',
            strokeWeight: 1.2,
          },
        })

        const ctrPct =
          p.impressions > 0
            ? ((p.clicks / p.impressions) * 100).toFixed(3)
            : '0.000'
        // eslint-disable-next-line no-undef
        const info = new google.maps.InfoWindow({
          content:
            '<div style="font-size:12px;line-height:1.5;">' +
            '<strong>' + p.area + '</strong><br/>' +
            'Spend: ' +
            p.spend.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) +
            '<br/>' +
            'Impressions: ' + p.impressions.toLocaleString() + '<br/>' +
            'Clicks: ' + p.clicks.toLocaleString() + '<br/>' +
            'CTR: ' + ctrPct + '%' +
            '</div>',
        })
        marker.addListener('click', () => info.open(this.$mapObject, marker))
        return marker
      })
    },
    applyGlowLayer() {
      if (!this.$mapObject || !this.$GlowOverlay) return
      this.clearGlow()
      const points = this.data
      if (!points.length) return
      const metric = this.metric

      const values = points.map((p) => p[metric])
      const rawMax = Math.max.apply(null, values) || 1
      const rawMin = Math.min.apply(null, values)
      const span = rawMax - rawMin || 1

      points.forEach((p) => {
        const ratio = (p[metric] - rawMin) / span
        const r = 255
        const g = Math.round(210 * (1 - ratio))
        const b = 0
        const color = 'rgba(' + r + ', ' + g + ', ' + b + ', 0.85)'
        const radiusMeters = 1800 + ratio * 2400

        const overlay = new this.$GlowOverlay(
          // eslint-disable-next-line no-undef
          new google.maps.LatLng(p.lat, p.lng),
          color,
          radiusMeters
        )
        overlay.setMap(this.$mapObject)
        this.$glowOverlays.push(overlay)
      })
    },
    rerender() {
      this.drawMarkers()
      this.applyGlowLayer()
    },
    clearMarkers() {
      if (this.$markers && this.$markers.length) {
        this.$markers.forEach((m) => m.setMap(null))
      }
      this.$markers = []
    },
    clearGlow() {
      if (this.$glowOverlays && this.$glowOverlays.length) {
        this.$glowOverlays.forEach((g) => g.setMap(null))
      }
      this.$glowOverlays = []
    },
    destroyMap() {
      this.clearMarkers()
      this.clearGlow()
      this.$mapObject = null
    },
  },
}
</script>

<template>
  <div ref="mapEl" :style="{ width: '100%', height: height, borderRadius: '6px' }" />
</template>
